import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { Destroyable } from '@frontend-site/shared/utils';
import { IPField } from '@panaya/ui/p-field';
import { LegacyCompatibilityType } from '../types/legacy-compatibility.types';

// ===================================================================================================
// This is a legacy class which will be removed once all using components have migrated
// to the publishable components library.
// Before changing it, consider migrating the p-field-* component you need into @panaya/ui instead:
// libs/publishable/ui/docs/maintainer/10-COMPONENT_MIGRATION.md
// ===================================================================================================
@Component({
    template: ``
})
export abstract class PFieldAbstractComponent<T> extends Destroyable implements ControlValueAccessor, IPField<T> {
    @Input() value: T | undefined;

    @Input() isReadOnly = false;

    @Input() isDisabled = false;

    @Input() isValid = true;

    @Input() ariaLabel?: string;

    @Input() valueTooltip: string = '';
    @Input() labelTooltip: string = '';

    @Input() shouldTransformLabelTextCase = true;

    @Output() public readonly viewValueChanged = new EventEmitter<T | undefined | LegacyCompatibilityType>();
    /**
     * @deprecated Please use {@link viewValueChanged} instead.
     */
    @Output() public readonly valueChange = this.viewValueChanged;

    touched = false;

    public setValue(value: T): void {
        this.value = value;
    }

    protected _onChange: (v: T | undefined) => void = (v) => {
        this.viewValueChanged.emit(v);
    };

    protected _onTouch: () => void = () => {
        this.cd.detectChanges();
    };

    constructor(protected cd: ChangeDetectorRef) {
        super();
    }

    protected markAsTouched() {
        if (!this.touched) {
            this._onTouch();
            this.touched = true;
        }
    }

    registerOnChange(fn: (v: T | undefined) => void): void {
        this._onChange = (v) => {
            fn(v);
            this.viewValueChanged.emit(v);
        };
    }

    registerOnTouched(fn: () => void): void {
        this._onTouch = () => {
            fn();
            this.cd.detectChanges();
        };
    }

    setDisabledState(isDisabled: boolean): void {
        // When programatically disabling the form control, by design we want our field to go into readonly mode.
        // (Also in pFieldAbstract) In Angular v15 setDisabledState is always called when a ControlValueAccessor is attached.
        // In our components, we don't want to match isReadOnly with the disabled state on init.
        //
        // This is a workaround to replicate the new config of Angular's FormsModule, which was not viable
        // as it requires refactoring all FormsModule imports in our code.
        // https://angular.io/guide/update-to-version-15#setdisabledstate-is-always-called-when-a-controlvalueaccessor-is-attached
        if (this.isReadOnly && !isDisabled) {
            return;
        }

        this.isReadOnly = isDisabled;
        this.cd.detectChanges();
    }

    writeValue(obj: T): void {
        this.value = obj;
        this.cd.detectChanges();
    }
}
